let cachedClientIndex = null;
let loadingClientPromise = null;

function splitArrayOrCsv(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  return String(val)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export async function getClientSearchIndex() {
  if (cachedClientIndex) return cachedClientIndex;
  if (loadingClientPromise) return loadingClientPromise;

  loadingClientPromise = (async () => {
    const res = await fetch('/data/search-index.json');
    if (!res.ok) {
      throw new Error(`Failed to fetch /data/search-index.json: ${res.status}`);
    }
    const raw = await res.json();

    const moviePosterBySlug = new Map();
    const movieYearBySlug = new Map();

    const movies = (raw.movies || []).map((m) => {
      const slug = m.slug || m.id;
      const poster = m.p || '';
      const year = m.y ? Number(m.y) : null;
      if (poster && slug) moviePosterBySlug.set(String(slug), poster);
      if (year && slug) movieYearBySlug.set(String(slug), year);
      return {
        id: m.id || slug,
        slug,
        title: m.t || '',
        year,
        language: m.l || 'Tamil',
        director: m.d || '',
        musicDirector: m.md || '',
        castNames: splitArrayOrCsv(m.c),
        posterUrl: poster,
        songCount: m.sc || 1,
        hasLyrics: true,
      };
    });

    const personMap = new Map();
    const registerPerson = (name, role) => {
      if (!name || typeof name !== 'string') return;
      const trimmed = name.trim();
      if (!trimmed || trimmed.toLowerCase() === 'unknown' || trimmed.toLowerCase() === 'various artists') return;
      const slug = trimmed
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      if (!slug || slug.length < 2) return;
      if (!personMap.has(slug)) {
        personMap.set(slug, {
          id: slug,
          slug,
          name: trimmed,
          role,
          count: 1,
        });
      } else {
        personMap.get(slug).count += 1;
      }
    };

    const songs = (raw.songs || []).map((s) => {
      const mSlug = s.ms || '';
      const poster = s.p || moviePosterBySlug.get(mSlug) || '';
      const year = s.y ? Number(s.y) : movieYearBySlug.get(mSlug) || null;
      const singersArr = splitArrayOrCsv(s.sg);
      const actorsArr = splitArrayOrCsv(s.ac);

      singersArr.forEach((n) => registerPerson(n, 'Singer'));
      splitArrayOrCsv(s.ly).forEach((n) => registerPerson(n, 'Lyricist'));
      splitArrayOrCsv(s.md).forEach((n) => registerPerson(n, 'Music Director'));
      actorsArr.forEach((n) => registerPerson(n, 'Actor'));

      return {
        id: s.id || s.slug,
        slug: s.slug || s.id,
        title: s.t || '',
        song_name: s.t || '',
        movieTitle: s.mt || '',
        movieSlug: mSlug,
        movieId: mSlug,
        year,
        language: s.l || 'Tamil',
        singers: singersArr,
        lyricist: s.ly || '',
        musicDirectorName: s.md || '',
        musicDirector: s.md || '',
        actors: actorsArr,
        mood: s.m || '',
        video_url: s.yt || '',
        posterUrl: poster,
        moviePosterUrl: poster,
        views: s.v || 0,
      };
    });

    movies.forEach((m) => {
      splitArrayOrCsv(m.director).forEach((n) => registerPerson(n, 'Director'));
      splitArrayOrCsv(m.musicDirector).forEach((n) => registerPerson(n, 'Music Director'));
      (m.castNames || []).forEach((n) => registerPerson(n, 'Actor'));
    });

    const persons = Array.from(personMap.values()).sort((a, b) => b.count - a.count);

    cachedClientIndex = { songs, movies, persons };
    return cachedClientIndex;
  })();

  return loadingClientPromise;
}
